import { useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Package, Plus, Trash2, UtensilsCrossed } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader, SectionHeader } from "@/components/ui/headers"
import { InlineInput } from "@/components/ui/inline-input"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  INVENTORY_PATHS,
  tInventory,
  type RecipeComponent,
  type SupplyOption,
} from "@/features/staff/inventory/inventory"
import { t } from "@/lib/i18n"
import { patchInline } from "@/lib/inline-update"

export type RecipesWorkspaceProps = {
  dishes: RecipeComponent[]
  addOns: RecipeComponent[]
  supplies: SupplyOption[]
  coverage: { covered: number; total: number }
}

const PAGE_SCOPE = "ui.react.pages.staff.location.manage.recipes.index"

/** `item_id` for a dish, `extra_id` for an add-on: the table carries both. */
type ComponentKind = "item" | "extra"

function AddLineRow({
  component,
  kind,
  supplies,
}: {
  component: RecipeComponent
  kind: ComponentKind
  supplies: SupplyOption[]
}) {
  const [supplyId, setSupplyId] = useState("")
  const [quantity, setQuantity] = useState("")

  const testId = `${kind}-${component.id}`
  const used = new Set(component.lines.map((line) => line.supplyId))
  // One line per (component, supply) is a unique index, and the merged entry a
  // recipe resolves is one row per supply — so a supply already on this recipe
  // is not offered again. Adding it would raise, and offering an option that
  // always raises is a trap with a nicer error message.
  const available = supplies.filter((supply) => !used.has(supply.id))

  const add = () => {
    if (!supplyId || quantity.trim().length === 0) return

    router.post(
      INVENTORY_PATHS.recipes,
      {
        [`${kind}_id`]: component.id,
        supply_id: Number(supplyId),
        quantity: quantity.trim(),
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setSupplyId("")
          setQuantity("")
        },
      }
    )
  }

  if (available.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2 pt-2">
      <Select value={supplyId} onValueChange={setSupplyId}>
        <SelectTrigger className="min-w-48 flex-1" data-testid={`${testId}-supply`}>
          <SelectValue placeholder={tInventory("recipe_supply_placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {available.map((supply) => (
            <SelectItem
              key={supply.id}
              value={String(supply.id)}
              data-testid={`${testId}-supply-option-${supply.id}`}
            >
              {supply.name} ({supply.unitLabel})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        inputMode="decimal"
        className="w-28"
        value={quantity}
        placeholder={tInventory("recipe_quantity_placeholder")}
        aria-label={tInventory("recipe_quantity_placeholder")}
        onChange={(event) => setQuantity(event.target.value)}
        data-testid={`${testId}-quantity`}
      />

      <Button
        type="button"
        variant="outline"
        disabled={!supplyId || quantity.trim().length === 0}
        onClick={add}
        data-testid={`${testId}-add`}
      >
        <Plus className="size-4" />
        {tInventory("recipe_add")}
      </Button>
    </div>
  )
}

function ComponentCard({
  component,
  kind,
  supplies,
  subtitle,
}: {
  component: RecipeComponent
  kind: ComponentKind
  supplies: SupplyOption[]
  subtitle: string | null
}) {
  const testId = `${kind}-${component.id}`

  return (
    <div className="py-3" data-testid={`recipe-${testId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0">
          <span className="truncate text-sm font-medium">{component.name}</span>
          {subtitle && <span className="text-muted-foreground ml-2 text-xs">{subtitle}</span>}
        </span>

        {component.lines.length === 0 ? (
          <StatusBadge
            tone="warning"
            label={tInventory("recipe_no_lines")}
            data-testid={`recipe-${testId}-empty`}
          />
        ) : (
          <span className="text-muted-foreground text-xs tabular-nums">
            {tInventory("recipe_lines_count", { count: component.lines.length })}
          </span>
        )}
      </div>

      {component.lines.length > 0 && (
        <ul className="mt-2 space-y-2">
          {component.lines.map((line) => (
            <li
              key={line.id}
              className="flex flex-wrap items-center gap-2"
              data-testid={`recipe-line-${line.id}`}
            >
              <span className="min-w-0 flex-1 truncate text-sm">{line.supplyName}</span>
              <InlineInput
                value={line.quantity}
                inputMode="decimal"
                className="w-28"
                aria-label={tInventory("recipe_quantity_placeholder")}
                successMessage={tInventory("recipe_quantity_updated")}
                data-testid={`recipe-line-${line.id}-quantity`}
                onSubmit={(value) =>
                  patchInline(INVENTORY_PATHS.recipe(line.id), { quantity: value })
                }
              />
              <span className="text-muted-foreground w-16 text-xs">{line.unitLabel}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={tInventory("recipe_remove")}
                data-testid={`recipe-line-${line.id}-remove`}
                onClick={() =>
                  router.delete(INVENTORY_PATHS.recipe(line.id), { preserveScroll: true })
                }
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <AddLineRow component={component} kind={kind} supplies={supplies} />
    </div>
  )
}

/**
 * What each dish and each add-on burns.
 *
 * ## The coverage figure is the headline, and that is why this is a screen
 *
 * A recipe belongs to a dish, so the obvious home for this is a panel inside the
 * item drawer. The obvious home hides the only number that decides whether the
 * whole feature may be trusted: how many dishes have a recipe **at all**. A
 * location with none has no writer of `recipe_consumption`, so stock only ever
 * goes up and every shortage the inventory screen could report is a data-entry
 * mistake. You cannot learn that by opening forty drawers.
 *
 * So the count is at the top and the list below renders every dish — including,
 * especially, the ones with nothing in them. The "Sin receta" badge is not a
 * warning about that dish; it is the shape of the gap in the figure above it.
 *
 * ## Add-ons are listed beside the dishes, not tucked away
 *
 * `Location::Order` freezes which extras the diner chose and `Location::Recipe`
 * resolves both sides, merging a shared supply into one entry — 50 g of queso in
 * the burger plus 30 g in "queso extra" is 80 g out of one tub. A screen that
 * only edited the dish side would look complete while under-consuming the
 * expensive half of every ticket, forever.
 *
 * ## Quantities commit inline; adding a line does not
 *
 * Changing 150 g to 160 g is a light operational edit and commits on blur.
 * Adding a line needs a supply and an amount together — half of that pair is not
 * a recipe line, it is a way to write 0 g of tomato — so it keeps its own button.
 */
export function RecipesWorkspace({
  dishes,
  addOns,
  supplies,
  coverage,
}: RecipesWorkspaceProps) {
  if (supplies.length === 0) {
    return (
      <MainContainer size="md">
        <div className="space-y-6">
          <PageHeader
            title={t(`${PAGE_SCOPE}.title`)}
            description={t(`${PAGE_SCOPE}.description`)}
          />
          <Card data-testid="recipes-no-supplies">
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <Package className="text-muted-foreground size-8" aria-hidden="true" />
              <p className="text-sm font-semibold">{tInventory("recipe_no_supplies_title")}</p>
              <p className="text-muted-foreground max-w-md text-sm">
                {tInventory("recipe_no_supplies_hint")}
              </p>
              <Button asChild data-testid="recipes-go-supplies">
                <Link href={INVENTORY_PATHS.supplies}>
                  {tInventory("recipe_no_supplies_action")}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainContainer>
    )
  }

  return (
    <MainContainer size="md">
      <div className="space-y-6">
        <PageHeader
          title={t(`${PAGE_SCOPE}.title`)}
          description={t(`${PAGE_SCOPE}.description`)}
        />

        <Card density="compact" data-testid="recipes-coverage-card">
          <CardContent className="space-y-1">
            <p className="text-muted-foreground text-xs">{tInventory("coverage_label")}</p>
            <p
              className="text-foreground text-sm font-semibold tabular-nums"
              data-testid="recipes-coverage"
            >
              {tInventory("coverage_value", {
                covered: coverage.covered,
                total: coverage.total,
              })}
            </p>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <SectionHeader title={tInventory("recipe_dishes_title")} className="px-0" />

          {dishes.length === 0 ? (
            <Card data-testid="recipes-no-dishes">
              <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
                <UtensilsCrossed className="text-muted-foreground size-8" aria-hidden="true" />
                <p className="text-sm font-semibold">{tInventory("recipe_no_dishes_title")}</p>
                <p className="text-muted-foreground max-w-md text-sm">
                  {tInventory("recipe_no_dishes_hint")}
                </p>
                <Button asChild data-testid="recipes-go-menu">
                  <Link href={INVENTORY_PATHS.menu}>{tInventory("recipe_no_dishes_action")}</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card density="compact" data-testid="recipes-dishes">
              <CardContent className="divide-border divide-y">
                {dishes.map((dish) => (
                  <ComponentCard
                    key={dish.id}
                    component={dish}
                    kind="item"
                    supplies={supplies}
                    subtitle={dish.categoryName ?? null}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-2">
          <SectionHeader title={tInventory("recipe_add_ons_title")} className="px-0" />
          <p className="text-muted-foreground text-sm">{tInventory("recipe_add_ons_hint")}</p>

          {addOns.length === 0 ? (
            <Card data-testid="recipes-no-add-ons">
              <CardContent className="py-10 text-center">
                <p className="text-muted-foreground text-sm">
                  {tInventory("recipe_add_ons_empty")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card density="compact" data-testid="recipes-add-ons">
              <CardContent className="divide-border divide-y">
                {addOns.map((addOn) => (
                  <ComponentCard
                    key={addOn.id}
                    component={addOn}
                    kind="extra"
                    supplies={supplies}
                    subtitle={addOn.groupName ?? null}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MainContainer>
  )
}
